import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useRef,
} from 'react';

import createVersioningSession, {
  VersionableModel,
  VersioningSession,
} from '@/utils/shared/createVersioningSession';

const VersioningContext = createContext<
  VersioningSession<VersionableModel> | undefined
>(undefined);

type VersioningProviderProps<T extends VersionableModel> = PropsWithChildren<{
  model: T;
  createVersion: (id: number) => Promise<T>;
  onVersioned?: (versioned: T, previous: T) => void;
}>;

export function VersioningProvider<T extends VersionableModel>({
  model,
  createVersion,
  onVersioned,
  children,
}: VersioningProviderProps<T>) {
  const callbacksRef = useRef({ createVersion, onVersioned });
  callbacksRef.current = { createVersion, onVersioned };

  const sessionRef = useRef<VersioningSession<T>>();
  sessionRef.current ??= createVersioningSession(model, {
    createVersion: id => callbacksRef.current.createVersion(id),
    onVersioned: (versioned, previous) =>
      callbacksRef.current.onVersioned?.(versioned, previous),
  });
  const session = sessionRef.current;

  useEffect(() => {
    if (model.id === session.getCurrent().id) session.setCurrent(model);
  }, [model, session]);

  return (
    <VersioningContext.Provider value={session}>
      {children}
    </VersioningContext.Provider>
  );
}

export function useOptionalVersioning<T extends VersionableModel>() {
  return useContext(VersioningContext) as VersioningSession<T> | undefined;
}

export function useVersioning<T extends VersionableModel>() {
  const session = useOptionalVersioning<T>();
  if (!session)
    throw new Error('useVersioning must be used within VersioningProvider');
  return session as VersioningSession<T>;
}
